import * as Location from 'expo-location';
import * as TaskManager from 'expo-task-manager';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { firestoreService } from './firestoreService';
import { Alert, Platform } from 'react-native';
import { UBICACION_SEGUNDO_PLANO } from '../constants/featureFlags';

const LOCATION_TASK_NAME = 'BACKGROUND_LOCATION_TASK';
const ACTIVE_DRIVER_KEY = 'GOSAFELY_ACTIVE_DRIVER_ID';
const ACTIVE_MODE_KEY = 'GOSAFELY_TRACKING_MODE';

/**
 * Modos de tracking GPS para optimizar batería del chofer.
 *
 * - IDLE: chofer online esperando viajes. Precisión moderada, intervalo
 *   amplio. Ahorra batería significativa (~50% menos consumo).
 * - ACTIVE_RIDE: chofer con un ride/delivery en curso. Precisión máxima
 *   y frecuencia alta para que el pasajero vea el marcador moverse fluido.
 *
 * El driver app cambia automáticamente entre modos:
 *   - toggleOnline → IDLE
 *   - aceptar oferta/delivery → ACTIVE_RIDE
 *   - completar/cancelar → IDLE
 *   - toggleOffline → stopTracking
 */
export type TrackingMode = 'IDLE' | 'ACTIVE_RIDE';

const MODE_CONFIG: Record<TrackingMode, {
  accuracy: Location.LocationAccuracy;
  timeInterval: number;
  distanceInterval: number;
  notificationBody: string;
}> = {
  IDLE: {
    accuracy: Location.Accuracy.Balanced,     // ~hundred meters, ahorra batería
    timeInterval: 15000,                       // cada 15s (vs 5s antes)
    distanceInterval: 30,                      // cada 30m (vs 10m antes)
    notificationBody: 'Disponible para recibir viajes. Modo ahorro de batería activo.',
  },
  ACTIVE_RIDE: {
    accuracy: Location.Accuracy.High,          // ~10m, calidad Uber
    timeInterval: 5000,                        // cada 5s para que el pasajero vea movimiento fluido
    distanceInterval: 10,
    notificationBody: 'Viaje en curso. Compartiendo tu ubicación por seguridad.',
  },
};

// Define the background task — corre fuera del ciclo de vida de React, por eso lee el
// driverId persistido en AsyncStorage en lugar de capturar una variable en el closure.
if (Platform.OS !== 'web') {
  TaskManager.defineTask(LOCATION_TASK_NAME, async ({ data, error }: any) => {
    if (error) {
      console.error('Error in background location task:', error);
      return;
    }
    if (!data) return;

    const { locations } = data;
    const location = locations?.[0];
    if (!location) return;

    try {
      const driverId = await AsyncStorage.getItem(ACTIVE_DRIVER_KEY);
      if (!driverId) {
        // Sin driverId no podemos escribir a Firestore sin contaminar otros docs
        return;
      }
      await firestoreService.updateDriverLocation(
        driverId,
        location.coords.latitude,
        location.coords.longitude
      );
    } catch (e) {
      console.error('Background location write failed:', e);
    }
  });
}

/**
 * Vigilante de PRIMER PLANO — el modo del envío a Google Play.
 *
 * Con `UBICACION_SEGUNDO_PLANO = false` (decisión del fundador hasta pasar la
 * revisión) el manifest no declara ACCESS_BACKGROUND_LOCATION, y
 * `startLocationUpdatesAsync` puede negarse a arrancar sin ese permiso. Este
 * vigilante hace el mismo trabajo —escribir la posición del chofer en
 * Firestore con la cadencia del modo— pero con `watchPositionAsync`, que solo
 * necesita el permiso de "mientras se usa".
 *
 * La diferencia práctica: el GPS vive mientras la app está abierta (aunque la
 * pantalla se apague poco, como trabaja un chofer con la app al frente) y se
 * detiene si Android mata el proceso. Para la revisión es exactamente lo que
 * queremos demostrar.
 */
let vigilanteForeground: Location.LocationSubscription | null = null;

// ═══════════════════════════════════════════════════════════════════════════
// EL ESTADO DEL VIGILANTE VIVE EN TRES VARIABLES. POR QUÉ CADA UNA:
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Contador de generación.
 *
 * `watchPositionAsync` es asíncrono: entre que se pide el vigilante y que
 * llega puede entrar otra llamada (el chofer se conecta y acepta un viaje casi
 * seguido, y `setTrackingMode` vuelve a llamar aquí). Antes las dos escribían
 * en `vigilanteForeground` y la segunda PISABA a la primera: el vigilante
 * perdido seguía vivo emitiendo posiciones y escribiendo en Firestore, y sin
 * referencia ya nadie podía apagarlo nunca. Con el contador, cada llamada se
 * queda con su número y solo conserva su suscripción si al aterrizar sigue
 * siendo la última; la que llegó tarde se apaga a sí misma.
 */
let generacionVigilante = 0;

/**
 * El modo que el sistema QUIERE tener puesto, aunque ahora mismo no haya
 * vigilante instalado (app en segundo plano). Es lo que se vuelve a aplicar al
 * regresar al primer plano.
 */
let modoVigilanteDeseado: TrackingMode | null = null;

/**
 * `true` SOLO cuando fuimos nosotros quienes soltamos el vigilante porque la
 * app pasó a segundo plano. Distingue "lo apagamos para que Android no nos
 * mate" de "el chofer se desconectó" — lo segundo no se reanuda solo.
 */
let suspendidoPorSegundoPlano = false;

/** Qué pasó al intentar devolverle el GPS al chofer. Ver `useAppStateGuard`. */
export type ResultadoReanudacion = 'REANUDADO' | 'NADA_QUE_HACER' | 'FALLO';

async function applyMode(mode: TrackingMode) {
  if (Platform.OS === 'web') return;
  const cfg = MODE_CONFIG[mode];

  if (!UBICACION_SEGUNDO_PLANO) {
    // La intención se apunta ANTES de cualquier await: si esta llamada acaba
    // perdiendo la carrera, el modo pedido no se pierde y la reanudación lo
    // respeta.
    modoVigilanteDeseado = mode;
    await AsyncStorage.setItem(ACTIVE_MODE_KEY, mode);

    // Con la app en segundo plano no se instala vigilante ninguno: se apunta
    // el modo y se aplica al volver. Sin esta guarda, un cambio de modo
    // disparado desde fuera de la pantalla (aceptar un viaje) reabriría el GPS
    // justo en el estado en el que Android nos mata por consumo.
    if (suspendidoPorSegundoPlano) return;

    const miGeneracion = ++generacionVigilante;

    // Relevar al vigilante anterior si el modo cambió.
    if (vigilanteForeground) {
      try { vigilanteForeground.remove(); } catch {}
      vigilanteForeground = null;
    }
    const nuevoVigilante = await Location.watchPositionAsync(
      {
        accuracy: cfg.accuracy,
        timeInterval: cfg.timeInterval,
        distanceInterval: cfg.distanceInterval,
      },
      async (location) => {
        try {
          const driverId = await AsyncStorage.getItem(ACTIVE_DRIVER_KEY);
          if (!driverId) return;
          await firestoreService.updateDriverLocation(
            driverId,
            location.coords.latitude,
            location.coords.longitude
          );
        } catch (e: any) {
          console.warn('[vigilanteForeground] no se pudo publicar la posición:', e?.message);
        }
      }
    );

    // La carrera se resuelve AQUÍ, ya con la suscripción en la mano: si
    // mientras esperábamos entró otra llamada —o nos mandaron al segundo
    // plano— esta suscripción sobra, y se apaga sola en vez de quedar viva sin
    // que nadie la pueda alcanzar.
    if (miGeneracion !== generacionVigilante || suspendidoPorSegundoPlano) {
      try { nuevoVigilante.remove(); } catch {}
      return;
    }
    vigilanteForeground = nuevoVigilante;
    return;
  }

  modoVigilanteDeseado = mode;

  await Location.startLocationUpdatesAsync(LOCATION_TASK_NAME, {
    accuracy: cfg.accuracy,
    timeInterval: cfg.timeInterval,
    distanceInterval: cfg.distanceInterval,
    showsBackgroundLocationIndicator: true,
    foregroundService: {
      notificationTitle: 'Go Safely',
      notificationBody: cfg.notificationBody,
      notificationColor: '#0A2540',
    },
  });
  await AsyncStorage.setItem(ACTIVE_MODE_KEY, mode);
}

// ═══════════════════════════════════════════════════════════════════════════
// UBICACIÓN DE RELLENO — CÓMO SE DISTINGUE DE UNA REAL
// ═══════════════════════════════════════════════════════════════════════════
//
// EL FALLO QUE ESTO CIERRA
// `getCurrentLocation()` nunca falla: cuando no hay permiso, el GPS no logra
// fix o revienta, devuelve el CENTRO DE SANTO DOMINGO (18.4861, -69.9312).
// Eso está bien para que el mapa tenga dónde encuadrarse, pero ese mismo valor
// se estaba usando como ORIGEN DEL VIAJE sin que nadie mirara nada: el chofer
// salía a recoger a alguien a la Zona Colonial mientras el pasajero esperaba
// en Santiago. Con pago en efectivo y un chofer que gasta gasolina, eso es
// dinero perdido y una reseña de una estrella.
//
// La convención anterior era "mira si `coords.accuracy` es null". Nadie la
// miraba porque no se veía: un objeto de relleno era indistinguible de uno
// real a simple vista. Ahora el relleno viene MARCADO y con motivo, y existe
// `obtenerOrigenDeViaje()`, que sencillamente NO devuelve coordenadas cuando
// no las sabe — así no hay forma de crear un viaje sobre un valor inventado.

/** Por qué no sabemos dónde está el usuario. */
export type MotivoRelleno = 'SIN_PERMISO' | 'TIEMPO_AGOTADO' | 'ERROR_GPS';

/**
 * Una ubicación de expo-location que, además, dice si es REAL o de relleno.
 * Sigue siendo un `Location.LocationObject`, así que todo el código que ya
 * existía compila igual; lo nuevo son los dos campos de procedencia.
 */
export type UbicacionApp = Location.LocationObject & {
  /** `true` SOLO en el valor de relleno. En un fix real este campo no existe. */
  esRelleno?: true;
  motivoRelleno?: MotivoRelleno;
};

/** Texto para el usuario, en su idioma y sin jerga. */
export const MENSAJE_RELLENO: Record<MotivoRelleno, string> = {
  SIN_PERMISO:
    'No tenemos permiso para ver tu ubicación, así que no sabemos dónde estás. Actívalo en los ajustes o escribe tu dirección a mano.',
  TIEMPO_AGOTADO:
    'El GPS no logró ubicarte. Sal a un lugar abierto e inténtalo de nuevo, o escribe tu dirección a mano.',
  ERROR_GPS:
    'No pudimos leer el GPS de este teléfono. Escribe tu dirección a mano para continuar.',
};

/**
 * ¿Esta ubicación es una lectura REAL del dispositivo?
 *
 * Devuelve `false` también cuando `accuracy` no es un número: eso significa
 * que el sistema operativo no sabe cuán buena es la posición, y "no lo
 * sabemos" no se rellena con un supuesto (por eso el relleno lleva
 * `accuracy: null`).
 */
export function esUbicacionReal(loc: UbicacionApp | null | undefined): boolean {
  if (!loc || loc.esRelleno === true) return false;
  const c: any = loc.coords;
  if (!c || typeof c.latitude !== 'number' || typeof c.longitude !== 'number') return false;
  return Number.isFinite(c.accuracy) && c.accuracy >= 0;
}

/**
 * Resultado de pedir el ORIGEN de un viaje. Cuando no se sabe, no hay
 * coordenadas que copiar por error: el caller está obligado a manejar `ok:false`.
 */
export type OrigenDeViaje =
  | {
      ok: true;
      latitud: number;
      longitud: number;
      /** Radio de incertidumbre en metros que reporta el sistema operativo. */
      precisionMetros: number;
      /**
       * `true` cuando el fix es real pero flojo (>200 m). No bloquea el viaje
       * —es una posición de verdad— pero conviene pedirle al pasajero que
       * confirme el punto de recogida antes de mandar al chofer.
       */
      bajaPrecision: boolean;
    }
  | { ok: false; motivo: MotivoRelleno; mensaje: string };

export const locationService = {
  /**
   * ¿Ya tenemos el permiso de ubicación en SEGUNDO PLANO?
   * Lo usa map.tsx para decidir si debe mostrar el AVISO DESTACADO
   * (prominent disclosure) antes de disparar el diálogo del sistema —
   * requisito obligatorio de Google Play (causa del rechazo 2026-07).
   */
  hasBackgroundPermission: async (): Promise<boolean> => {
    if (Platform.OS === 'web') return true;
    try {
      const { status } = await Location.getBackgroundPermissionsAsync();
      return status === 'granted';
    } catch {
      return false;
    }
  },

  /**
   * Permiso de ubicación MIENTRAS SE USA LA APP. Nada más.
   *
   * ⚠️ ESTA FUNCIÓN NO DEBE PEDIR NUNCA EL DE SEGUNDO PLANO.
   *
   * Antes pedía los dos seguidos. Como `getCurrentLocation()` la llama por
   * dentro, y el mapa llama a `getCurrentLocation()` al montarse, CUALQUIER
   * usuario —incluido un pasajero que solo está mirando, o el revisor de
   * Google en modo invitado— recibía el diálogo de "permitir todo el tiempo"
   * en el segundo cero, sin ninguna explicación previa.
   *
   * Ese es, literalmente, el motivo del rechazo de julio de 2026: pedir
   * ACCESS_BACKGROUND_LOCATION sin divulgación destacada. El aviso destacado
   * existía (map.tsx), pero solo cubría el botón "Conectarse" del chofer — el
   * diálogo del sistema ya había salido mucho antes por este camino.
   */
  requestPermissions: async () => {
    const { status } = await Location.requestForegroundPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permiso Denegado', 'Se requiere acceso a la ubicación para buscar viajes cercanos.');
      return false;
    }
    return true;
  },

  /**
   * Permiso de ubicación EN SEGUNDO PLANO. Solo lo llama `startTracking`, y
   * solo después de que el usuario haya aceptado el aviso destacado.
   *
   * Google exige que la divulgación destacada aparezca ANTES de este diálogo y
   * que el usuario haga algo afirmativo. No lo llames desde ningún otro sitio.
   */
  requestBackgroundPermission: async (): Promise<boolean> => {
    // DECISIÓN DEL FUNDADOR: hasta que Google apruebe la app, la ubicación es
    // SOLO mientras se usa. Con la bandera apagada este diálogo no sale nunca
    // — y el manifest ni siquiera declara el permiso, así que pedirlo sería
    // además inútil. Ver UBICACION_SEGUNDO_PLANO en constants/featureFlags.
    if (!UBICACION_SEGUNDO_PLANO) return false;
    if (Platform.OS === 'web') return true;
    const { status } = await Location.requestBackgroundPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert(
        'Ubicación en segundo plano desactivada',
        'Sin este permiso tu señal se pierde cuando minimizas la app y el pasajero deja de verte. Puedes activarlo en los ajustes del teléfono.'
      );
      return false;
    }
    return true;
  },

  /**
   * Arranca tracking en modo IDLE por defecto (ahorro de batería).
   * Para subir a alta precisión durante un viaje, llama `setTrackingMode('ACTIVE_RIDE')`.
   */
  startTracking: async (driverId: string, mode: TrackingMode = 'IDLE') => {
    if (!driverId) {
      console.warn('startTracking llamado sin driverId — abortando para no contaminar Firestore');
      return;
    }

    const hasPermission = await locationService.requestPermissions();
    if (!hasPermission) return;

    if (Platform.OS === 'web') return; // Background tracking unsupported on web

    // Conectarse es un acto deliberado del chofer, con la app delante: cierra
    // cualquier suspensión que hubiera quedado colgando de una sesión anterior
    // en segundo plano. Si no, `applyMode` se limitaría a apuntar el modo y el
    // chofer se quedaría "conectado" pero sin emitir posición.
    suspendidoPorSegundoPlano = false;

    // AQUÍ, y solo aquí, se pide el de segundo plano: es el único punto al que
    // se llega después de que el chofer haya aceptado el aviso destacado y
    // pulsado "Conectarse". Antes venía arrastrado dentro de
    // `requestPermissions()`, así que le saltaba a todo el mundo al abrir el
    // mapa. Si lo niega, seguimos igual: el tracking en primer plano funciona
    // y el chofer ya recibió la explicación de qué pierde.
    await locationService.requestBackgroundPermission();

    await AsyncStorage.setItem(ACTIVE_DRIVER_KEY, driverId);

    // Si ya hay una task registrada con otro modo, la detenemos antes de relanzar
    const registered = await TaskManager.isTaskRegisteredAsync(LOCATION_TASK_NAME);
    if (registered) {
      await Location.stopLocationUpdatesAsync(LOCATION_TASK_NAME);
    }
    await applyMode(mode);
  },

  /**
   * Cambia el modo de tracking sin reiniciar todo el sistema GPS.
   * Llamar al aceptar/completar un ride o delivery.
   */
  setTrackingMode: async (mode: TrackingMode) => {
    if (Platform.OS === 'web') return;

    // ── POR QUÉ ESTA COMPROBACIÓN CAMBIÓ ───────────────────────────────────
    // Antes preguntaba SIEMPRE por la tarea de SEGUNDO PLANO. Pero con
    // `UBICACION_SEGUNDO_PLANO = false` —la configuración con la que se va a
    // Google Play— esa tarea NO SE REGISTRA NUNCA, así que esta función se
    // salía por aquí en todas las llamadas y no hacía absolutamente nada.
    //
    // Dos consecuencias que llevaban tiempo activas sin que nadie lo viera:
    //   · La precisión alta al aceptar un viaje NUNCA se aplicaba: el pasajero
    //     veía el carro a saltos de 15 segundos y con hasta 100 m de error.
    //   · El modo nunca se guardaba, así que nada podía saber si había un
    //     viaje en curso. El vigilante de segundo plano nuevo dependía de ese
    //     dato y creía que el chofer estaba libre incluso con pasajero a bordo.
    //
    // Ahora, sin permiso de segundo plano, lo que manda es el vigilante de
    // primer plano: si está vivo, se le aplica el modo.
    if (!UBICACION_SEGUNDO_PLANO) {
      if (!vigilanteForeground) return;   // no hay rastreo activo: nada que cambiar
      await applyMode(mode);
      return;
    }

    const registered = await TaskManager.isTaskRegisteredAsync(LOCATION_TASK_NAME);
    if (!registered) {
      // No hay tracking activo: nada que cambiar.
      return;
    }
    // Re-aplicar config con el nuevo modo (Expo no expone updateLocationUpdatesAsync,
    // pero startLocationUpdatesAsync sobre una task ya registrada la actualiza).
    await applyMode(mode);
  },

  /** Devuelve el modo actual o null si no hay tracking activo. */
  getCurrentMode: async (): Promise<TrackingMode | null> => {
    if (Platform.OS === 'web') return null;
    // Mismo motivo que en `setTrackingMode`: sin permiso de segundo plano la
    // tarea no existe, y preguntar por ella devolvía null aunque el chofer
    // estuviera en pleno viaje. Quien manda entonces es el vigilante de primer
    // plano.
    if (!UBICACION_SEGUNDO_PLANO) {
      if (!vigilanteForeground) return null;
      const guardado = await AsyncStorage.getItem(ACTIVE_MODE_KEY);
      return guardado === 'ACTIVE_RIDE' ? 'ACTIVE_RIDE' : 'IDLE';
    }
    const registered = await TaskManager.isTaskRegisteredAsync(LOCATION_TASK_NAME);
    if (!registered) return null;
    const stored = await AsyncStorage.getItem(ACTIVE_MODE_KEY);
    return (stored === 'ACTIVE_RIDE' || stored === 'IDLE') ? stored : 'IDLE';
  },

  stopTracking: async () => {
    if (Platform.OS === 'web') return;
    // Apagar también al vigilante de primer plano (el modo del envío a Play).
    // El contador de generación sube para que cualquier `watchPositionAsync`
    // todavía en vuelo se encuentre con un número viejo al aterrizar y se
    // apague solo: si no, apagar el GPS justo mientras se estaba encendiendo
    // dejaba un vigilante huérfano emitiendo con el chofer ya desconectado.
    generacionVigilante++;
    modoVigilanteDeseado = null;
    suspendidoPorSegundoPlano = false;
    if (vigilanteForeground) {
      try { vigilanteForeground.remove(); } catch {}
      vigilanteForeground = null;
    }
    const isRegistered = await TaskManager.isTaskRegisteredAsync(LOCATION_TASK_NAME);
    if (isRegistered) {
      await Location.stopLocationUpdatesAsync(LOCATION_TASK_NAME);
    }
    // Limpiar el ID y el modo activos
    await AsyncStorage.multiRemove([ACTIVE_DRIVER_KEY, ACTIVE_MODE_KEY]);
  },

  /**
   * El modo con el que se está rastreando AHORA, o `null` si no hay nada que
   * rastrear (pasajero, chofer desconectado). Síncrono a propósito: el
   * guardián del estado de la app tiene que decidir qué soltar en el mismo
   * instante en que Android manda la app al fondo, sin esperar a un await.
   *
   * No confundir con `getCurrentMode()`, que mira la task de SEGUNDO PLANO y
   * hoy —con `UBICACION_SEGUNDO_PLANO = false`— siempre devuelve null.
   */
  modoDeRastreoActual: (): TrackingMode | null => modoVigilanteDeseado,

  /**
   * Suelta el vigilante de GPS porque la app se fue a segundo plano.
   *
   * POR QUÉ: sin el permiso de segundo plano (decisión del fundador hasta
   * pasar la revisión de Play), Android deja de entregar posiciones en cuanto
   * la app deja de estar delante. El vigilante no muere: se queda ocupando
   * memoria nativa y, cada vez que el sistema le da un respiro, escribe DOS
   * documentos en Firestore por posición. Es decir: gasta y no sirve. Soltarlo
   * no le quita al chofer ninguna capacidad que Android le estuviera dando.
   *
   * Devuelve `true` si de verdad había algo que soltar.
   */
  suspenderPorSegundoPlano: async (): Promise<boolean> => {
    if (Platform.OS === 'web') return false;
    // Con el permiso de segundo plano de verdad, el servicio en primer plano
    // de Android es legítimo y visible (notificación permanente): eso es justo
    // lo que el chofer autorizó, y no se toca.
    if (UBICACION_SEGUNDO_PLANO) return false;
    if (!vigilanteForeground) return false;

    // Invalida cualquier `watchPositionAsync` en vuelo: cuando aterrice verá
    // una generación vieja y se apagará solo en vez de instalarse con la app
    // ya minimizada.
    generacionVigilante++;
    try { vigilanteForeground.remove(); } catch {}
    vigilanteForeground = null;
    suspendidoPorSegundoPlano = true;
    return true;
  },

  /**
   * Devuelve el GPS al volver al primer plano. ESTA ES LA MITAD PELIGROSA:
   * un chofer sin vigilante desaparece del mapa del pasajero, que es peor que
   * el problema que estamos evitando. Por eso:
   *
   *  - Solo reanuda lo que suspendimos NOSOTROS (`suspendidoPorSegundoPlano`).
   *    Si el chofer se desconectó, no se le resucita el GPS por la espalda.
   *  - Si ya no hay `driverId` guardado, es que hubo `stopTracking` mientras
   *    estábamos fuera: no hay nada que reanudar.
   *  - CONSULTA el permiso, no lo pide: el usuario pudo revocarlo en los
   *    ajustes mientras la app estaba minimizada, y `requestPermissions()`
   *    levantaría un Alert encima de la pantalla justo al volver.
   *  - Si algo falla, se queda marcado como suspendido para volver a
   *    intentarlo en el próximo regreso al primer plano.
   */
  reanudarDesdeSegundoPlano: async (): Promise<ResultadoReanudacion> => {
    if (Platform.OS === 'web') return 'NADA_QUE_HACER';
    if (UBICACION_SEGUNDO_PLANO) return 'NADA_QUE_HACER';
    if (!suspendidoPorSegundoPlano) return 'NADA_QUE_HACER';

    const driverId = await AsyncStorage.getItem(ACTIVE_DRIVER_KEY);
    if (!driverId) {
      suspendidoPorSegundoPlano = false;
      modoVigilanteDeseado = null;
      return 'NADA_QUE_HACER';
    }

    try {
      const { status } = await Location.getForegroundPermissionsAsync();
      if (status !== 'granted') {
        console.warn('[locationService] no se reanuda el GPS: permiso revocado mientras la app estaba fuera');
        return 'FALLO'; // sigue suspendido → se reintenta al próximo regreso
      }
    } catch {
      return 'FALLO';
    }

    const guardado = await AsyncStorage.getItem(ACTIVE_MODE_KEY);
    const modo: TrackingMode =
      modoVigilanteDeseado ?? (guardado === 'ACTIVE_RIDE' ? 'ACTIVE_RIDE' : 'IDLE');

    try {
      // Bajar la bandera ANTES: si no, `applyMode` se limitaría a apuntar el
      // modo y volveríamos con el chofer invisible.
      suspendidoPorSegundoPlano = false;
      await applyMode(modo);
      return 'REANUDADO';
    } catch (e: any) {
      suspendidoPorSegundoPlano = true;
      console.warn('[locationService] falló la reanudación del GPS:', e?.message);
      return 'FALLO';
    }
  },

  /**
   * Obtiene la ubicación inicial del usuario CON FRENO DE EMERGENCIA.
   *
   * Race condition de producción: expo-location NO acepta `timeout` en
   * `getCurrentPositionAsync` — si el GPS no logra fix la promise queda
   * pendiente indefinidamente.
   *
   * Estrategia (fresh=false, default → rápido):
   *   1) Cache fresco del SO (≤30s). Si existe, devolverlo de inmediato.
   *   2) En paralelo, lanzar un fix BALANCED con timeout 20s.
   *   3) Si timeout, fallback a Santo Domingo.
   *
   * Estrategia (fresh=true → botón "Mi ubicación"):
   *   - Ignorar cache, forzar fix HIGH con timeout 20s.
   *
   * Notas:
   *   - `Accuracy.Balanced` resuelve en 2-5s en exterior con torres celulares,
   *     mucho mejor que `Lowest` que daba precisión >1km.
   *   - El relleno de SD NUNCA se debe confundir con la ubicación real. Ya no
   *     hay que adivinarlo mirando `accuracy`: viene marcado con
   *     `esRelleno: true` + `motivoRelleno`. Comprueba `esUbicacionReal(loc)`.
   *   - Para el ORIGEN DE UN VIAJE no uses esta función: usa
   *     `obtenerOrigenDeViaje()`, que no devuelve coordenadas inventadas.
   *
   * NUNCA retorna null — siempre coords válidas → el render del mapa nunca
   * se queda atorado.
   */
  getCurrentLocation: async (fresh: boolean = false): Promise<UbicacionApp> => {
    // Coords de Santo Domingo como red de seguridad PARA ENCUADRAR EL MAPA.
    // `esRelleno: true` es la marca que impide que se cuele como ubicación
    // real; `accuracy: null` se mantiene porque de verdad no sabemos cuán
    // buena es (no lo es en absoluto) y porque el código viejo lo miraba.
    const relleno = (motivo: MotivoRelleno): UbicacionApp => ({
      coords: {
        latitude: 18.4861,
        longitude: -69.9312,
        altitude: null,
        accuracy: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
      },
      timestamp: Date.now(),
      esRelleno: true,
      motivoRelleno: motivo,
    });

    try {
      const hasPermission = await locationService.requestPermissions();
      if (!hasPermission) {
        console.warn('[getCurrentLocation] sin permiso → relleno SD');
        return relleno('SIN_PERMISO');
      }

      // 1) Cache reciente del SO — solo si no piden fresh.
      //    30s de margen evita usar fixes viejos al cambiar de barrio.
      if (!fresh) {
        try {
          const cached = await Location.getLastKnownPositionAsync({ maxAge: 30000 });
          if (cached && cached.coords.accuracy && cached.coords.accuracy < 200) {
            // Solo aceptamos cache con accuracy <200m. Si el último fix
            // fue malo (interior, túnel), preferimos pedir uno nuevo.
            return cached;
          }
        } catch {
          // ignored — pasamos al GPS real
        }
      }

      // 2) GPS real. Balanced en background = rápido + razonable. Para
      //    el botón "Mi ubicación" (fresh=true) subimos a High.
      const accuracy = fresh ? Location.Accuracy.High : Location.Accuracy.Balanced;
      const gpsPromise = Location.getCurrentPositionAsync({ accuracy });
      const timeoutPromise = new Promise<UbicacionApp>((resolve) => {
        setTimeout(() => {
          console.warn('[getCurrentLocation] timeout 20s → relleno SD');
          resolve(relleno('TIEMPO_AGOTADO'));
        }, 20000);
      });

      return await Promise.race([gpsPromise, timeoutPromise]);
    } catch (e: any) {
      console.warn('[getCurrentLocation] error → relleno SD:', e?.message);
      return relleno('ERROR_GPS');
    }
  },

  /**
   * ORIGEN DE UN VIAJE — o coordenadas reales, o nada.
   *
   * Úsala en TODO sitio donde el número acabe siendo un punto al que se manda
   * a una persona: crear un viaje, pedir un delivery, un SOS, compartir "estoy
   * aquí". A diferencia de `getCurrentLocation()`, esta NUNCA devuelve el
   * centro de Santo Domingo disfrazado de posición del usuario: si no lo
   * sabemos, devuelve `ok:false` con el motivo y un texto listo para enseñar,
   * y el flujo debe pedirle la dirección al usuario en vez de inventarla.
   *
   * @param fresh `true` ignora la caché del sistema y fuerza un fix de alta
   *              precisión (hasta 20 s). Por defecto acepta una lectura real
   *              de hasta 30 s de antigüedad y menos de 200 m de error.
   */
  obtenerOrigenDeViaje: async (fresh: boolean = false): Promise<OrigenDeViaje> => {
    const loc = await locationService.getCurrentLocation(fresh);
    if (!esUbicacionReal(loc)) {
      const motivo: MotivoRelleno = loc.motivoRelleno ?? 'ERROR_GPS';
      return { ok: false, motivo, mensaje: MENSAJE_RELLENO[motivo] };
    }
    const precisionMetros = Number(loc.coords.accuracy);
    return {
      ok: true,
      latitud: loc.coords.latitude,
      longitud: loc.coords.longitude,
      precisionMetros,
      bajaPrecision: precisionMetros > 200,
    };
  },

  /**
   * Suscripción puntual: empieza con un fix rápido cacheado y entrega
   * actualizaciones de mayor precisión cuando lleguen. Diseñado para el
   * arranque del mapa — el user ve el marcador moverse del centro de la
   * ciudad a su barrio real conforme el GPS afina el fix.
   *
   * Devuelve la unsubscribe function.
   */
  watchInitialLocation: async (
    onUpdate: (loc: Location.LocationObject) => void
  ): Promise<() => void> => {
    if (Platform.OS === 'web') return () => {};
    const hasPermission = await locationService.requestPermissions();
    if (!hasPermission) return () => {};

    // Sub Foreground en modo High durante los primeros 30s para conseguir
    // el fix más preciso posible. Después podemos detenerla — el tracking
    // ya cubrido por el background task del chofer (si aplica).
    const sub = await Location.watchPositionAsync(
      {
        accuracy: Location.Accuracy.High,
        timeInterval: 2000,
        distanceInterval: 5,
      },
      (loc) => {
        if (loc?.coords?.accuracy && loc.coords.accuracy < 100) {
          onUpdate(loc);
        }
      }
    );
    // Auto-stop después de 30s para no quemar batería.
    //
    // El temporizador se guarda y se cancela en la baja: antes quedaba vivo
    // aunque el llamador ya hubiera cerrado la suscripción, así que cada
    // arranque del mapa dejaba un timer nativo pendiente 30 s apuntando a una
    // suscripción ya muerta. Con la pantalla montándose y desmontándose se
    // van apilando sin que nada los recoja.
    let temporizadorParada: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      temporizadorParada = null;
      try { sub.remove(); } catch {}
    }, 30000);
    return () => {
      if (temporizadorParada) {
        clearTimeout(temporizadorParada);
        temporizadorParada = null;
      }
      try { sub.remove(); } catch {}
    };
  }
};
